import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';

export interface UseBulkOperationsParams {
  bucketName: string;
  selectedFiles: Set<string>;
  /** Called after a successful bulk operation to invalidate cache */
  onSuccess: (operation: 'delete' | 'move') => void;
}

export interface UseBulkOperationsReturn {
  showBulkDeleteModal: boolean;
  showBulkMoveModal: boolean;
  openDeleteModal: () => void;
  closeDeleteModal: () => void;
  openMoveModal: () => void;
  closeMoveModal: () => void;
  /**
   * Callback wired to BulkDeleteModal's onSuccess prop.
   * Shows appropriate toast and calls onSuccess('delete').
   *
   * `resumableMessage` is supplied only for an unfinished-but-resumable folder delete
   * (`FOLDER_DELETE_RESUMABLE`). Such a response can carry an empty `failedKeys` — the
   * server ran out of its time budget with nothing archive-blocked — so the count alone
   * cannot distinguish it from a full success. When it is present the success toast is
   * suppressed: the modal has already surfaced the unfinished wording through `onError`,
   * and a plain success toast alongside it would contradict that (requirement 2.20).
   * The listing is still refreshed so the objects that were removed disappear from view.
   */
  handleBulkDeleteSuccess: (
    deletedCount: number,
    failedKeys: string[],
    resumableMessage?: string
  ) => void;
  /**
   * Callback wired to BulkMoveModal's onSuccess prop.
   * Shows appropriate toast and calls onSuccess('move').
   * When all failed entries share the same reason the reason is shown in the toast
   * so the user knows why (e.g. file too large) rather than just which file failed.
   */
  handleBulkMoveSuccess: (movedCount: number, failed: Array<{ key: string; reason: string }>) => void;
}

/**
 * Manages modal open/close state and success callbacks for bulk delete and bulk move operations.
 * The actual API calls are delegated to BulkDeleteModal and BulkMoveModal components.
 *
 * @param bucketName   - The S3 bucket being operated on
 * @param selectedFiles - The set of currently selected file keys
 * @param onSuccess    - Called after a successful bulk operation to trigger cache invalidation
 */
export function useBulkOperations({
  bucketName,
  selectedFiles,
  onSuccess,
}: UseBulkOperationsParams): UseBulkOperationsReturn {
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showBulkMoveModal, setShowBulkMoveModal] = useState(false);

  const openDeleteModal = useCallback(() => setShowBulkDeleteModal(true), []);
  const closeDeleteModal = useCallback(() => setShowBulkDeleteModal(false), []);
  const openMoveModal = useCallback(() => setShowBulkMoveModal(true), []);
  const closeMoveModal = useCallback(() => setShowBulkMoveModal(false), []);

  const handleBulkDeleteSuccess = useCallback(
    (deletedCount: number, failedKeys: string[], resumableMessage?: string) => {
      // An unfinished folder delete already reached the user through the modal's onError
      // call. Emitting any success toast here — plain or mixed — would tell them the
      // delete finished when it did not (requirement 2.20).
      const isResumable = resumableMessage !== undefined;

      if (!isResumable) {
        if (failedKeys.length === 0) {
          toast.success(`${deletedCount} files deleted successfully`);
        } else {
          toast.success(`${deletedCount} of ${deletedCount + failedKeys.length} files deleted`);
          toast.error(`Failed to delete: ${failedKeys.join(', ')}`);
        }
      }
      closeDeleteModal();
      onSuccess('delete');
    },
    [closeDeleteModal, onSuccess]
  );

  const handleBulkMoveSuccess = useCallback(
    (movedCount: number, failed: Array<{ key: string; reason: string }>) => {
      if (failed.length === 0) {
        toast.success(`${movedCount} ${movedCount === 1 ? 'file' : 'files'} moved successfully`);
      } else {
        if (movedCount > 0) {
          toast.success(`${movedCount} of ${movedCount + failed.length} ${movedCount + failed.length === 1 ? 'file' : 'files'} moved`);
        }
        // Group failures by reason so each distinct reason gets one toast.
        // This avoids spamming N toasts when N large files all hit the time-budget limit.
        const byReason = new Map<string, string[]>();
        for (const { key, reason } of failed) {
          const existing = byReason.get(reason) ?? [];
          existing.push(key.split('/').pop() ?? key);
          byReason.set(reason, existing);
        }
        Array.from(byReason.entries()).forEach(([reason, names]) => {
          const label = names.length === 1 ? names[0] : `${names.length} files`;
          toast.error(`${label}: ${reason}`, { duration: 8000 });
        });
      }
      closeMoveModal();
      onSuccess('move');
    },
    [closeMoveModal, onSuccess]
  );

  return {
    showBulkDeleteModal,
    showBulkMoveModal,
    openDeleteModal,
    closeDeleteModal,
    openMoveModal,
    closeMoveModal,
    handleBulkDeleteSuccess,
    handleBulkMoveSuccess,
  };
}
